
import { BackendQuestion } from '../types';
import { StorageService } from './storageService';
import { MOCK_POOL } from '../data/mockData';

const STORAGE_KEY = 'cna_missed_questions_vault_v3';

interface VaultItem {
  id: string;
  streak: number; 
  timestamp: number;
}

const getVaultData = (): VaultItem[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
        const oldStored = localStorage.getItem('cna_missed_questions_vault_v2') || localStorage.getItem('cna_missed_questions_vault');
        if (oldStored) {
            const raw = JSON.parse(oldStored);
            const migrated = Array.isArray(raw) ? raw.map(item => {
                if (typeof item === 'string') return { id: item, streak: 0, timestamp: Date.now() };
                return item;
            }) : [];
            saveVaultData(migrated);
            return migrated;
        }
        return [];
    }
    return JSON.parse(stored);
  } catch (e) {
    console.error("Failed to parse missed questions vault", e);
    return [];
  }
};

const saveVaultData = (data: VaultItem[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  window.dispatchEvent(new CustomEvent('missed-questions-updated', { detail: { count: data.length } }));
};

const getCleanId = (id: string): string => {
    if (!id) return '';
    // Strip all session-specific suffixes added by getMockSession or AI generation
    return id.split('_prac_')[0].split('_inst_')[0].split('_gen_')[0].split('_ai_')[0].split('_skill_')[0];
};

export const MissedQuestionsService = {
  getCount: (): number => {
    return getVaultData().length;
  },

  getQuestions: async (certId: string): Promise<BackendQuestion[]> => {
    const data = getVaultData();
    if (data.length === 0) return [];
    
    const uniqueIds = Array.from(new Set(data.map(item => item.id)));
    const mockIds = uniqueIds.filter(id => id.startsWith('mock_'));
    const registryIds = uniqueIds.filter(id => !id.startsWith('mock_'));
    
    let combinedPool: BackendQuestion[] = [];

    // 1. Fetch from Registry (Remote DB)
    if (registryIds.length > 0) {
        try {
            const res = await StorageService.getQuestions(certId, { ids: registryIds.join(','), limit: 100 });
            if (res && res.items) combinedPool = [...res.items];
        } catch (e) {
            console.error("Registry partition unreachable for vault items.", e);
        }
    }

    // 2. Fetch from Mock Pool (Local Fallback for 'cna')
    if (mockIds.length > 0 && (certId === 'cna' || !certId)) {
        const localMatches = MOCK_POOL.filter(q => mockIds.includes(q._id));
        combinedPool = [...combinedPool, ...localMatches];
    }
    
    // Remove duplicates if any
    const seen = new Set();
    return combinedPool.filter(q => {
        const id = getCleanId(q._id);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
    });
  },

  getStreak: (questionId: string): number => {
    const realId = getCleanId(questionId);
    const data = getVaultData();
    const item = data.find(i => i.id === realId);
    return item ? item.streak : 0;
  },

  addQuestion: (questionId: string) => {
    const realId = getCleanId(questionId);
    if (!realId) return;
    const data = getVaultData();
    const existingIndex = data.findIndex(i => i.id === realId);

    if (existingIndex > -1) {
      data[existingIndex].streak = 0;
      data[existingIndex].timestamp = Date.now();
    } else {
      data.push({ id: realId, streak: 0, timestamp: Date.now() });
    }
    saveVaultData(data);
  },

  recordSuccess: (questionId: string): boolean => {
    const realId = getCleanId(questionId);
    const data = getVaultData();
    const existingIndex = data.findIndex(i => i.id === realId);

    if (existingIndex === -1) return false;

    const currentStreak = (data[existingIndex].streak || 0) + 1;
    
    if (currentStreak >= 2) {
        const newData = data.filter(i => i.id !== realId);
        saveVaultData(newData);
        return true; 
    } else {
        data[existingIndex].streak = currentStreak;
        saveVaultData(data);
        return false; 
    }
  },

  clearAll: (silent: boolean = false) => {
    if (silent || confirm("Permanently purge the Mistake Vault? This will reset all error tracking.")) {
        localStorage.removeItem(STORAGE_KEY);
        window.dispatchEvent(new CustomEvent('missed-questions-updated', { detail: { count: 0 } }));
        return true;
    }
    return false;
  }
};
